import React, { useState } from 'react';
import '../Masters/EmailTemplate/AddEmailTemplate.css';

interface EmailTagInputProps {
    label: string;
    emails: string[];
    onChange: (emails: string[]) => void;
    error?: string;
    disabled?: boolean;
    placeholder?: string;
    required?: boolean;
}

const EmailTagInput: React.FC<EmailTagInputProps> = ({
    label,
    emails,
    onChange,
    error,
    disabled,
    placeholder = 'Enter email address and press Enter or comma',
    required = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');

    const addEmail = (rawEmail: string) => {
        const trimmed = rawEmail.trim().replace(/^,|,$/g, '');
        if (!trimmed) return;

        const parts = trimmed.split(',').map((e) => e.trim()).filter(Boolean);
        const validEmails: string[] = [];
        let invalidFound = false;

        parts.forEach((email) => {
            const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
            if (isValid) {
                if (!emails.includes(email)) {
                    validEmails.push(email);
                }
            } else {
                invalidFound = true;
            }
        });

        if (invalidFound) {
            setInputError('email format not valid');
        } else {
            setInputError('');
        }

        if (validEmails.length > 0) {
            onChange([...emails, ...validEmails]);
            setInputValue('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['Enter', ',', 'Tab', ' '].includes(e.key)) {
            e.preventDefault();
            addEmail(inputValue);
        } else if (e.key === 'Backspace' && !inputValue && emails.length > 0 && !disabled) {
            onChange(emails.slice(0, -1));
        }
    };

    const handleBlur = () => {
        if (inputValue) {
            addEmail(inputValue);
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        addEmail(pastedText);
    };

    const removeEmail = (indexToRemove: number) => {
        if (disabled) return;
        onChange(emails.filter((_, idx) => idx !== indexToRemove));
    };

    const displayError = error || inputError;

    return (
        <div className="email-tag-input">
            <label className="email-tag-input__label">
                {label} {required && <span className="email-tag-input__required">*</span>}
            </label>

            <div
                className={[
                    'email-tag-input__field',
                    displayError ? 'email-tag-input__field--error' : '',
                    disabled ? 'email-tag-input__field--disabled' : '',
                ].filter(Boolean).join(' ')}
            >
                {emails.map((email, idx) => (
                    <span key={idx} className="email-tag-input__chip">
                        {email}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => removeEmail(idx)}
                                title="Remove email"
                                className="email-tag-input__remove-button"
                            >
                                &times;
                            </button>
                        )}
                    </span>
                ))}

                {!disabled && (
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            if (inputError) setInputError('');
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        onPaste={handlePaste}
                        placeholder={emails.length === 0 ? placeholder : ''}
                        className="email-tag-input__input"
                    />
                )}
            </div>

            {displayError && <div className="email-tag-input__error">{displayError}</div>}
        </div>
    );
};

export default EmailTagInput;
